<template>
  <div v-if="hasProducts" class="p-4 my-2 bg-white rounded-md">
    <div class="flex gap-2 pb-2">
      <niceone-image :src="$publicPath('/images/cart-buttons/promocode.svg')" width="25"/>
      <div class="font-niceone-medium">{{ $t('cart.promo_code') }}</div>
    </div>
    <div class="relative">
      <div v-if="isApplied || hasCoupon">
        <div
            class="h-12 w-full border-2 border-[#EDEDED] rounded-lg bg-[#F5F5F5] flex justify-between items-center px-3 py-2">
          <div class="flex gap-2 items-center">
            <div class="!max-w-44 truncate whitespace-nowrap overflow-hidden">
              {{ activeCode }}
            </div>
            <div v-if="!applyLoading && !cartLoading">
              <niceone-image v-if="errorMessages" @click="handleRemove"
                             :src="$publicPath('/images/cart-buttons/error.svg')"
                             width="16"/>
              <niceone-image v-else :src="$publicPath('/images/cart-buttons/correct.svg')" width="14"/>
            </div>
          </div>
          <van-loading v-if="applyLoading || removeLoading" size="24"/>
          <div v-else>
            <div v-if="!coupon" @click="applyPromo"
                 class="cursor-pointer text-sm font-niceone-medium text-primary tracking-wide">
              {{ $t('cart.apply') }}
            </div>
            <div v-else @click="handleRemove"
                 class="text-sm font-niceone-medium cursor-pointer text-discount-color tracking-wide">
              {{ $t('cart.remove') }}
            </div>
          </div>
        </div>
      </div>
      <van-field v-else v-model="promoCode" @keydown.enter="applyPromo" required clearable
                 :placeholder="$t('cart.promo_code_placeholder')"
                 class="border-2 !border-[#EDEDED] rounded-lg !bg-[#F5F5F5]">
        <template #button>
          <div @click="applyPromo" class="cursor-pointer text-sm font-niceone-medium text-primary tracking-wide">
            {{ $t('cart.apply') }}
          </div>
        </template>
      </van-field>
      <div v-if="errorMessages">
        <div v-for="error in errorMessages" :key="error" class="text-discount-color text-sm mt-2">
          {{ error }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {useCartStore} from '@/store/cart'
import {useCheckoutStore} from '@/store/checkout'
import {applyCouponMutation, removeCouponMutation} from '@/plugins/API/checkout'
import {getCartQuery} from '@/plugins/API/cart'
import {COOKIE_KEYS} from '~/constants/cookie'
import {useAuthStore} from '@/store/auth'

const cartStore = useCartStore()
const checkoutStore = useCheckoutStore()

const {cart, cartProducts} = storeToRefs(cartStore)
const {refetch, isLoading: cartLoading} = getCartQuery()

const promoCode = ref(cart.value?.coupon || '')
const isApplied = ref(!!cart.value?.coupon)
const errorMessages = ref('')

const {isLoading: applyLoading, mutateAsync: applyCoupon} = applyCouponMutation()
const {isLoading: removeLoading, mutateAsync: removeCoupon} = removeCouponMutation()

const authStore = useAuthStore();
const {user} = storeToRefs(authStore)

const coupon = computed(() => cart.value?.coupon)
const hasCoupon = computed(() => !!coupon.value)
const hasProducts = computed(() => !!cartProducts.value?.length)
const activeCode = computed(() => coupon.value || promoCode.value)

const route = useRoute()
const isAllowCheckoutCall = computed(() => useNuxtApp().$device.isDesktop && user.value && route.path.endsWith('checkout'))

const applyPromo = async () => {
  isApplied.value = true
  try {
    await applyCoupon(promoCode.value)
    await refetch()
    if (isAllowCheckoutCall.value) checkoutStore.triggerGetCheckout()
    errorMessages.value = ''
  } catch (err) {
    errorMessages.value = err?.response?.data?.error
  }
}

const removePromo = async () => {
  try {
    await removeCoupon()
    await refetch()
    if (isAllowCheckoutCall.value) checkoutStore.triggerGetCheckout()
  } catch (err) {
  }
}

const resetPromo = () => {
  promoCode.value = ''
  errorMessages.value = ''
  isApplied.value = false
}

const handleRemove = () => {
  if (hasCoupon.value) removePromo()
  resetPromo()
}

onMounted(() => {
  const rewardCode = useCookie(COOKIE_KEYS.REWARD_CODE, {secure: true})
  if (rewardCode.value) {
    promoCode.value = rewardCode.value
    rewardCode.value = null
    applyPromo()
  }
})

watch(coupon, (newCoupon) => {
  if (!newCoupon) resetPromo()
})

watch(cartProducts, () => {
  if (!cartProducts.value?.length && coupon.value) {
    removePromo()
  }
})
</script>
